/**
 * Инвентаризация — Phase 4.
 *
 * Список инвентаризаций + детали выбранной + создание новой.
 * Жизненный цикл: draft → applied / cancelled.
 *
 * Применение апдейтит warehouse_stocks.quantity = actual_qty для всех
 * посчитанных позиций с расхождением, пишет audit-запись.
 */
import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { api, ApiError } from "@/api/client";
import { fetchSections, formatRub } from "@/api/warehouse";
import { usePermissions } from "@/lib/usePermissions";


type StocktakeStatus = "draft" | "applied" | "cancelled";

interface StocktakeRow {
  id: number;
  warehouse: string;
  section: string | null;
  status: StocktakeStatus;
  started_at: string;
  applied_at: string | null;
  started_by: string | null;
  applied_by: string | null;
  note: string | null;
  items_count: number;
  diff_count: number;
}

interface StocktakeItem {
  id: number;
  stocktake_id: number;
  item_id: number;
  item_name: string;
  item_sku: string | null;
  item_unit: string | null;
  item_section: string | null;
  system_qty: number;
  actual_qty: number | null;
  diff: number | null;
  price_buy: number | null;
  note: string | null;
}

interface StocktakeDetail {
  doc: StocktakeRow;
  items: StocktakeItem[];
  summary: {
    total: number;
    counted: number;
    remaining: number;
    diffs: number;
    qty_over: number;
    qty_short: number;
    diff_value_buy: number;
  };
}

const STATUS_LABEL: Record<StocktakeStatus, string> = {
  draft:     "Черновик",
  applied:   "Применена",
  cancelled: "Отменена",
};
const STATUS_COLOR: Record<StocktakeStatus, string> = {
  draft:     "#e99a12",
  applied:   "#3D7A3D",
  cancelled: "var(--mute)",
};


// API hooks
function fetchStocktakes(): Promise<{ items: StocktakeRow[] }> {
  return api(`/api/warehouse/stocktakes`);
}
function fetchStocktake(id: number): Promise<StocktakeDetail> {
  return api(`/api/warehouse/stocktakes/${id}`);
}
function createStocktake(payload: { warehouse: string; section?: string | null; note?: string | null; only_with_stock?: boolean }) {
  return api<{ id: number; items_count: number }>(`/api/warehouse/stocktakes`, {
    method: "POST", body: JSON.stringify(payload),
  });
}
function updateStocktakeItem(stid: number, itemId: number, payload: { actual_qty: number | null; note?: string | null }) {
  return api(`/api/warehouse/stocktakes/${stid}/items/${itemId}`, {
    method: "PATCH", body: JSON.stringify(payload),
  });
}
function applyStocktake(stid: number) {
  return api<{ stocktake_id: number; items_applied: number }>(`/api/warehouse/stocktakes/${stid}/apply`, { method: "POST" });
}
function cancelStocktake(stid: number) {
  return api(`/api/warehouse/stocktakes/${stid}`, { method: "DELETE" });
}


export function WarehouseStocktakesPage() {
  const can = usePermissions();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [creatingOpen, setCreatingOpen] = useState(false);

  const listQ = useQuery({
    queryKey: ["warehouse-stocktakes"],
    queryFn: fetchStocktakes,
  });

  if (listQ.isLoading) return <Spinner size="lg" label="Загрузка инвентаризаций…" />;
  if (listQ.error) {
    const err = listQ.error;
    if (err instanceof ApiError && err.status === 403) {
      return <div className="alert alert-amber"><div className="alert-body">Доступ ограничен</div></div>;
    }
    return <div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div>;
  }
  const list = listQ.data?.items || [];

  return (
    <div className="page">

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "var(--gap)" }}>
        <div className="muted" style={{ fontSize: 12 }}>
          Инвентаризация → сверка факт vs система. Применение перезаписывает остатки на «факт» + пишет audit.
        </div>
        {can("stocktake.create") && (
          <button type="button" className="btn btn-primary"
            onClick={() => setCreatingOpen(true)}
            style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 14px", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            + Новая инвентаризация
          </button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: "40px 20px" }}>
          <div className="muted">Инвентаризаций ещё не было</div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: selectedId != null ? "minmax(360px, 480px) 1fr" : "1fr", gap: "var(--gap)" }}>
          <div className="card" style={{ padding: 0 }}>
            <div className="tbl-scroll">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Склад</th>
                    <th>Раздел</th>
                    <th style={{ width: 110 }}>Статус</th>
                    <th style={{ width: 110 }}>Начата</th>
                    <th className="num" style={{ width: 90 }}>Поз. / расх.</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(st => (
                    <tr key={st.id}
                        onClick={() => setSelectedId(st.id)}
                        style={{ cursor: "pointer", background: st.id === selectedId ? "var(--paper)" : undefined }}>
                      <td>{st.warehouse}</td>
                      <td style={{ fontSize: 12 }}>{st.section || <span className="muted">все</span>}</td>
                      <td>
                        <span style={{ display: "inline-block", padding: "1px 8px", fontSize: 11,
                                       borderRadius: 10, background: "var(--paper)",
                                       color: STATUS_COLOR[st.status], fontWeight: 600 }}>
                          {STATUS_LABEL[st.status]}
                        </span>
                      </td>
                      <td className="tabular" style={{ fontSize: 12 }}>{st.started_at.slice(0, 10)}</td>
                      <td className="num tabular" style={{ fontSize: 12 }}>
                        {st.items_count}
                        {st.diff_count > 0 && <span style={{ color: "var(--brick)" }}> · {st.diff_count}Δ</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {selectedId != null && (
            <StocktakeDetails id={selectedId} onClose={() => setSelectedId(null)} />
          )}
        </div>
      )}

      {creatingOpen && (
        <StocktakeCreateModal onClose={() => setCreatingOpen(false)}
                              onCreated={(id) => { setSelectedId(id); setCreatingOpen(false); }} />
      )}
    </div>
  );
}


function StocktakeDetails({ id, onClose }: { id: number; onClose: () => void }) {
  const can = usePermissions();
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["warehouse-stocktake", id],
    queryFn: () => fetchStocktake(id),
  });

  const [search, setSearch] = useState("");
  const [showOnlyMissing, setShowOnlyMissing] = useState(true);

  const applyMut = useMutation({
    mutationFn: () => applyStocktake(id),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ["warehouse-stocktake", id] });
      void qc.invalidateQueries({ queryKey: ["warehouse-stocktakes"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-stocks"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-overview"] });
      void qc.invalidateQueries({ queryKey: ["warehouse-planning"] });
      alert(`Применено: ${data.items_applied} позиций скорректировано`);
    },
  });
  const cancelMut = useMutation({
    mutationFn: () => cancelStocktake(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-stocktakes"] });
      onClose();
    },
  });
  const updateMut = useMutation({
    mutationFn: (vars: { itemId: number; actual_qty: number | null }) =>
      updateStocktakeItem(id, vars.itemId, { actual_qty: vars.actual_qty }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["warehouse-stocktake", id] });
    },
  });

  if (q.isLoading) return <div className="card" style={{ padding: 16 }}><Spinner /></div>;
  if (!q.data) return null;
  const { doc, items, summary } = q.data;
  const isDraft = doc.status === "draft";
  const filter = search.trim().toLowerCase();
  const visibleItems = items.filter(it => {
    if (showOnlyMissing && isDraft && it.actual_qty !== null) return false;
    if (!filter) return true;
    return (it.item_name || "").toLowerCase().includes(filter)
        || (it.item_sku || "").toLowerCase().includes(filter);
  });

  return (
    <div className="card" style={{ padding: 16, position: "relative" }}>
      <button type="button" onClick={onClose}
        style={{ position: "absolute", top: 8, right: 8, background: "transparent", border: "none", fontSize: 20, color: "var(--mute)", cursor: "pointer" }}>×</button>
      <div style={{ fontFamily: "var(--serif)", fontSize: 20, fontWeight: 700 }}>
        Инвентаризация № {doc.id}
      </div>
      <div style={{ fontSize: 12, color: "var(--mute)", marginTop: 4, marginBottom: 12 }}>
        Склад: <strong>{doc.warehouse}</strong>
        {doc.section && <> · Раздел: <strong>{doc.section}</strong></>}
        {" · "}Начата: {doc.started_at.slice(0, 16).replace("T", " ")}
        {doc.started_by && <> · {doc.started_by}</>}
        {doc.note && <div style={{ marginTop: 4 }}>{doc.note}</div>}
      </div>

      {/* Summary */}
      <div className="row row-4" style={{ marginBottom: 12 }}>
        <KPI label="Позиций" value={`${summary.total}`} foot={`${summary.counted} посчитано · ${summary.remaining} осталось`} />
        <KPI label="Излишек" value={`${summary.qty_over}`} foot="ед., факт > системы" />
        <KPI label="Недостача" value={`${summary.qty_short}`} foot="ед., факт < системы" />
        <KPI label="Стоимость расхождения" value={formatRub(summary.diff_value_buy)} foot="по закуп. цене" />
      </div>

      {isDraft && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          {can("stocktake.apply") && (
            <button type="button" className="btn btn-sm btn-primary"
                    onClick={() => { if (confirm(`Применить инвентаризацию? Это перезапишет остатки на «факт».`)) applyMut.mutate(); }}
                    disabled={applyMut.isPending || summary.counted === 0}>
              {applyMut.isPending ? "Применяется…" : `Применить (${summary.diffs} изменений)`}
            </button>
          )}
          <button type="button" className="btn btn-sm"
                  onClick={() => { if (confirm("Отменить инвентаризацию?")) cancelMut.mutate(); }}
                  style={{ color: "var(--brick)" }}>
            Отменить
          </button>
          <div style={{ flex: 1 }} />
          <input type="search" className="input-sm" placeholder="Поиск по позиции"
                 value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 220 }} />
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12 }}>
            <input type="checkbox" checked={showOnlyMissing} onChange={e => setShowOnlyMissing(e.target.checked)} />
            Только не посчитанные
          </label>
        </div>
      )}

      <div className="tbl-scroll" style={{ maxHeight: "60vh" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Артикул</th>
              <th>Наименование</th>
              <th>Раздел</th>
              <th className="num" style={{ width: 90 }}>Система</th>
              <th className="num" style={{ width: 110 }}>Факт</th>
              <th className="num" style={{ width: 90 }}>Δ</th>
            </tr>
          </thead>
          <tbody>
            {visibleItems.length === 0 ? (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: 30, color: "var(--mute)" }}>
                {showOnlyMissing && isDraft ? "Все позиции посчитаны" : "Ничего не найдено"}
              </td></tr>
            ) : visibleItems.map(it => (
              <StocktakeRowEdit key={it.id} item={it} editable={isDraft}
                                onSave={(v) => updateMut.mutate({ itemId: it.item_id, actual_qty: v })} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function StocktakeRowEdit({ item, editable, onSave }: {
  item: StocktakeItem;
  editable: boolean;
  onSave: (val: number | null) => void;
}) {
  const [draft, setDraft] = useState<string>(item.actual_qty != null ? String(item.actual_qty) : "");
  const diff = item.actual_qty != null ? item.actual_qty - item.system_qty : null;
  const diffColor = diff == null ? "var(--mute)"
    : diff > 0 ? "#3D7A3D"
    : diff < 0 ? "var(--brick)"
    : "var(--mute)";

  return (
    <tr>
      <td className="tabular">{item.item_sku || "—"}</td>
      <td>{item.item_name}</td>
      <td style={{ fontSize: 12, color: "var(--mute)" }}>{item.item_section || "—"}</td>
      <td className="num tabular">{item.system_qty} {item.item_unit || ""}</td>
      <td className="num">
        {editable ? (
          <input
            type="text" inputMode="decimal" className="input-sm"
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={() => {
              const trimmed = draft.trim();
              const newVal = trimmed === "" ? null : parseFloat(trimmed.replace(",", "."));
              if (newVal !== null && isNaN(newVal)) return;
              const cur = item.actual_qty;
              if (newVal === cur) return;
              onSave(newVal);
            }}
            placeholder={String(item.system_qty)}
            style={{ width: "100%", textAlign: "right", fontSize: 12 }}
          />
        ) : (
          item.actual_qty != null ? `${item.actual_qty} ${item.item_unit || ""}` : "—"
        )}
      </td>
      <td className="num tabular strong" style={{ color: diffColor }}>
        {diff == null ? "—" : (diff > 0 ? `+${diff}` : `${diff}`)}
      </td>
    </tr>
  );
}


function KPI({ label, value, foot }: { label: string; value: string; foot?: string }) {
  return (
    <div className="kpi">
      <div className="kpi-label">{label}</div>
      <div className="kpi-value kpi-value-sm">{value}</div>
      {foot && <div className="kpi-foot"><span style={{ fontSize: 11, color: "var(--mute)" }}>{foot}</span></div>}
    </div>
  );
}


function StocktakeCreateModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: number) => void }) {
  const [warehouse, setWarehouse] = useState("СКЛАД 2_4");
  const [section, setSection] = useState<string>("");
  const [onlyWithStock, setOnlyWithStock] = useState(true);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);

  const sectionsQ = useQuery({
    queryKey: ["warehouse-sections"],
    queryFn: () => fetchSections(),
  });

  const mut = useMutation({
    mutationFn: () => createStocktake({
      warehouse, section: section || null,
      only_with_stock: onlyWithStock,
      note: note.trim() || null,
    }),
    onSuccess: (data) => {
      if (data.items_count === 0) {
        setError("В выборке нет позиций — измените фильтры");
        return;
      }
      onCreated(data.id);
    },
    onError: (e) => setError(e instanceof Error ? e.message : String(e)),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    mut.mutate();
  }

  return (
    <div role="dialog" onClick={onClose}
         style={{ position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.4)", display: "grid", placeItems: "center", padding: 16 }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="card"
            style={{ width: "100%", maxWidth: 480, padding: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16 }}>
          <div style={{ fontFamily: "var(--serif)", fontSize: 22, fontWeight: 700 }}>Новая инвентаризация</div>
          <button type="button" onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, cursor: "pointer", color: "var(--mute)" }}>×</button>
        </div>

        <div style={{ marginBottom: 10 }}>
          <Label>Склад *</Label>
          <select className="input-sm" style={{ width: "100%" }} value={warehouse}
                  onChange={e => setWarehouse(e.target.value)} required>
            <option value="СКЛАД 2_4">СКЛАД 2_4</option>
            <option value="ИНСТРУМЕНТ 1.0">ИНСТРУМЕНТ 1.0</option>
            <option value="ОФИС">ОФИС</option>
          </select>
        </div>

        <div style={{ marginBottom: 10 }}>
          <Label>Раздел (опц.) — только позиции этого раздела</Label>
          <select className="input-sm" style={{ width: "100%" }} value={section}
                  onChange={e => setSection(e.target.value)}>
            <option value="">— Все разделы —</option>
            {(sectionsQ.data || []).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, marginBottom: 10 }}>
          <input type="checkbox" checked={onlyWithStock} onChange={e => setOnlyWithStock(e.target.checked)} />
          Только позиции с остатком &gt; 0 (рекомендуется)
        </label>

        <div style={{ marginBottom: 12 }}>
          <Label>Примечание</Label>
          <textarea className="input-sm" style={{ width: "100%", minHeight: 50 }}
                    value={note} onChange={e => setNote(e.target.value)}
                    placeholder='Например: «Месячная сверка», «Сверка после грозы», ...' />
        </div>

        {error && <div className="alert alert-red" style={{ marginBottom: 8 }}><div className="alert-body">{error}</div></div>}

        <div style={{ textAlign: "right" }}>
          <button type="button" className="btn" onClick={onClose} style={{ marginRight: 8 }}>Отмена</button>
          <button type="submit" className="btn btn-primary" disabled={mut.isPending}
                  style={{ background: "var(--brick)", color: "#fff", border: "none", padding: "6px 18px", fontWeight: 600, cursor: "pointer" }}>
            {mut.isPending ? "Создание…" : "Создать (черновик)"}
          </button>
        </div>
      </form>
    </div>
  );
}


function Label({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 12, fontWeight: 600, color: "var(--ink-3)", marginBottom: 4 }}>{children}</div>;
}
